import { DeleteOutlineRounded, MoreHorizRounded } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RolesMap, TripRole, UserData } from '../../../firebase/firebase.types';
import { getUserDocumentsWithIds } from '../../../firebase/users.utils';
import useAnchorMenu from '../../../hooks/useAnchorMenu';
import { useAppDispatch } from '../../../redux/hooks';
import { deleteTrip } from '../../../redux/trip/trip.slice';
import { getTripDates } from '../../../utils/getTripDates';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import LetterAvatarGroup from '../../LetterAvatar/LetterAvatarGroup';
import DropdownMenu from '../../shared/DropdownMenu';
import './TripsListItem.scss';

interface TripsListItemProps {
  tripId: string;
  title: string;
  location: string | unknown;
  startDateISO?: string;
  endDateISO?: string;
  userRoles: RolesMap;
  canDelete?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
}

function TripsListItem({
  tripId,
  title,
  location,
  startDateISO,
  endDateISO,
  userRoles,
  canDelete,
  onClick,
}: TripsListItemProps) {
  const dispatch = useAppDispatch();
  const menu = useAnchorMenu();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);

  // Memoize tripDates
  const tripDates = useMemo(
    () => getTripDates(startDateISO, endDateISO, true),
    [startDateISO, endDateISO]
  );

  // Memoize event handlers
  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setShowDeleteDialog(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteTrip({ tripId }));
    setShowDeleteDialog(false);
  }, [dispatch, tripId]);

  useEffect(() => {
    let isSubscribed = true;
    const fetchEmails = async () => {
      const usersDocs = await getUserDocumentsWithIds(Object.keys(userRoles)).catch(() => null);
      if (!(isSubscribed && usersDocs)) {
        return;
      }
      // Owner first. A member whose document has no email yet falls back to their name, so
      // they still get an avatar.
      const members: { isOwner: boolean; label: string }[] = [];
      usersDocs.forEach((userDoc) => {
        const data = userDoc.data() as UserData;
        const label = data.email || data.displayName;
        if (label) {
          members.push({ isOwner: userRoles[userDoc.id] === TripRole.OWNER, label });
        }
      });
      members.sort((a, b) => Number(b.isOwner) - Number(a.isOwner));
      setEmails(members.map((member) => member.label));
    };
    if (userRoles) {
      fetchEmails();
    }
    return () => {
      isSubscribed = false;
    };
  }, [userRoles]);

  return (
    <>
      <div
        className='trips-list-item-container'
        onClick={onClick}
      >
        <Tooltip
          title={title}
          enterDelay={500}
          disableHoverListener={title.length <= 45}
        >
          <div className='trips-list-title'>{title}</div>
        </Tooltip>
        {canDelete && (
          <div className='trips-list-more-options-container'>
            <Button
              className='trips-list-more-button'
              onClick={menu.open}
            >
              <MoreHorizRounded />
            </Button>
            <DropdownMenu {...menu.menuProps}>
              <DropdownMenu.Item
                tone='danger'
                icon={<DeleteOutlineRounded fontSize='small' />}
                onSelect={handleDeleteClick}
              >
                Delete
              </DropdownMenu.Item>
            </DropdownMenu>
          </div>
        )}
        <div className='trips-list-middle-section'>
          <div className='trips-list-location-container'>
            {typeof location === 'string' && location.length > 0 && (
              <>
                <span className='location-emoji'>📍</span>
                <span className='trips-list-location'>{location}</span>
              </>
            )}
          </div>
          <div className='trips-list-dates'>
            {tripDates || <span className='trips-list-dates-placeholder'>No dates selected</span>}
          </div>
        </div>
        <div className='trips-list-bottom-section'>
          {emails.length > 1 && (
            <div className='trips-list-avatars-group'>
              <LetterAvatarGroup userRoles={emails} />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        title='Are you sure?'
        confirmLabel='Delete'
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        destructive
      >
        Deleting <strong>{title}</strong> cannot be undone.
      </ConfirmDialog>
    </>
  );
}

TripsListItem.displayName = 'TripsListItem';
export default TripsListItem;
